import { applyRate, BASIS_POINTS_PER_UNIT, divideRounded, splitEvenly, sumBy, } from '../money/money.js';
import { keyedEnum } from '../catalogs/enum.js';
import { buildDueDates, PERIODS_PER_YEAR } from './frequency.js';
import { allowsPrincipalGrace, FULL_PRINCIPAL_SHARE, principalPlanViolations, REPAYMENT_METHOD, REPAYMENT_METHOD_LABELS, } from './repayment.js';
/**
 * Motor de planes de pago. Combina dos decisiones independientes:
 * - cómo se calcula el interés (`InterestMethod`: sobre saldos o flat), y
 * - cómo se devuelve el capital (`RepaymentMethod`: cuota nivelada, capital
 *   constante, capital al vencimiento, pago único o plan personalizado).
 * Incluye gracia a capital, IVA sobre intereses y cuota fija para reamortizar.
 */
/**
 * Métodos de cálculo de interés:
 * - `declining_balance` (sobre saldos / sistema francés): cuotas iguales,
 *   interés sobre el saldo pendiente. Es el valor por defecto: transparente y
 *   bien visto por el regulador.
 * - `flat` (interés plano / sobre monto original): interés sobre el capital
 *   original en cada período. Aún es común entre ONG y prestamistas rurales;
 *   se conserva para que las organizaciones migren fielmente su cartera.
 */
export const INTEREST_METHODS = ['declining_balance', 'flat'];
/** Catálogo con clave de métodos de interés: `INTEREST_METHOD.flat`. */
export const INTEREST_METHOD = keyedEnum(INTEREST_METHODS);
/** Etiquetas en español de cada método de interés. */
export const INTEREST_METHOD_LABELS = {
    declining_balance: 'Sobre saldos (cuota nivelada)',
    flat: 'Flat (sobre monto original)',
};
/** Número de la primera cuota de un plan nuevo (las cuotas se numeran desde 1). */
export const FIRST_PERIOD_NUMBER = 1;
/** Interés de un período sobre un saldo, redondeado al centavo. */
export function periodInterest(balance, annualRate, frequency) {
    return accruedInterest(balance, annualRate, frequency, 1);
}
/**
 * Interés simple de `periods` períodos sobre un saldo constante, redondeado
 * una sola vez al final (así un pago único no acumula errores de centavo).
 */
export function accruedInterest(balance, annualRate, frequency, periods) {
    return divideRounded(balance * annualRate * periods, BASIS_POINTS_PER_UNIT * PERIODS_PER_YEAR[frequency]);
}
/**
 * Cuota nivelada (capital + interés) que amortiza `principal` en `periods`
 * períodos a `annualRate`: P·r / (1 − (1 + r)^−n). Con tasa cero se reparte
 * el capital redondeando hacia arriba.
 */
export function levelInstallment(principal, annualRate, frequency, periods) {
    if (periods <= 0)
        throw new RangeError('El plazo debe ser de al menos un período');
    const rate = annualRate / BASIS_POINTS_PER_UNIT / PERIODS_PER_YEAR[frequency];
    if (rate === 0)
        return Math.ceil(principal / periods);
    return Math.round((principal * rate) / (1 - Math.pow(1 + rate, -periods)));
}
/**
 * Reparte el capital según porcentajes por período usando el acumulado:
 * cada porción es la diferencia entre el capital acumulado redondeado hasta
 * ese período y el anterior. Así la suma es exactamente el capital y el
 * redondeo nunca se acumula.
 */
export function principalFromPlan(principal, plan) {
    let cumulativeShare = 0;
    let cumulativePrincipal = 0;
    return plan.map((share) => {
        cumulativeShare += share;
        const reachedPrincipal = divideRounded(principal * cumulativeShare, FULL_PRINCIPAL_SHARE);
        const part = reachedPrincipal - cumulativePrincipal;
        cumulativePrincipal = reachedPrincipal;
        return part;
    });
}
/** Fechas en que realmente se cobra: un pago único solo cobra al vencimiento. */
function collectionDates(input) {
    if (input.repaymentMethod !== REPAYMENT_METHOD.single_payment)
        return input.dueDates;
    const maturity = input.dueDates[input.dueDates.length - 1];
    return maturity ? [maturity] : [];
}
/**
 * Capital fijo de cada fila de cobro para las formas de pago que no dependen
 * del interés. Devuelve `null` en la cuota nivelada sobre saldos, cuyo
 * capital se calcula fila por fila (cuota − interés).
 */
function fixedPrincipalParts(input, rowCount) {
    const { principal, principalGracePeriods: grace } = input;
    const zeros = (count) => Array.from({ length: count }, () => 0);
    switch (input.repaymentMethod) {
        case REPAYMENT_METHOD.level_installment:
            // Con interés flat la cuota nivelada es simplemente capital parejo.
            return input.interestMethod === INTEREST_METHOD.flat ? [...zeros(grace), ...splitEvenly(principal, rowCount - grace)] : null;
        case REPAYMENT_METHOD.constant_principal:
            return [...zeros(grace), ...splitEvenly(principal, rowCount - grace)];
        case REPAYMENT_METHOD.interest_only_bullet:
        case REPAYMENT_METHOD.single_payment:
            return [...zeros(rowCount - 1), principal];
        case REPAYMENT_METHOD.custom_principal:
            return input.principalAmounts ? [...input.principalAmounts] : principalFromPlan(principal, input.principalPlan ?? []);
    }
}
/** Valida la combinación de forma de pago, plazo, gracia y plan; lanza `RangeError` si no es coherente. */
function assertAmortizable(input) {
    const periods = input.dueDates.length;
    const grace = input.principalGracePeriods;
    if (periods === 0)
        throw new RangeError('El plan necesita al menos una fecha de pago');
    if (grace < 0 || grace >= periods)
        throw new RangeError('Los períodos de gracia deben ser menores que el plazo');
    if (grace > 0 && !allowsPrincipalGrace(input.repaymentMethod)) {
        throw new RangeError(`La forma de pago "${REPAYMENT_METHOD_LABELS[input.repaymentMethod]}" no admite gracia a capital`);
    }
    if (input.repaymentMethod !== REPAYMENT_METHOD.custom_principal)
        return;
    if (input.principalAmounts) {
        if (input.principalAmounts.length !== periods || sumBy(input.principalAmounts, (amount) => amount) !== input.principal) {
            throw new RangeError('El capital por período debe tener una entrada por cuota y sumar el capital');
        }
        return;
    }
    const violations = principalPlanViolations(input.principalPlan, periods);
    if (violations.length > 0)
        throw new RangeError(violations.join('. '));
}
/**
 * Construye un plan de pagos. La última fila siempre absorbe el redondeo, de
 * modo que el saldo final es exactamente cero y Σ capital = capital prestado.
 */
export function amortize(input) {
    assertAmortizable(input);
    const { principal, annualInterestRate, frequency, taxRate } = input;
    const dates = collectionDates(input);
    const grace = input.principalGracePeriods;
    const firstPeriodNumber = input.firstPeriodNumber ?? FIRST_PERIOD_NUMBER;
    const isFlat = input.interestMethod === INTEREST_METHOD.flat;
    // Un pago único devenga todos los períodos del plazo en su única cuota.
    const accrualPeriods = input.repaymentMethod === REPAYMENT_METHOD.single_payment ? (input.accrualPeriods ?? input.dueDates.length) : 1;
    const fixedParts = fixedPrincipalParts(input, dates.length);
    const level = input.fixedInstallment ?? levelInstallment(principal, annualInterestRate, frequency, dates.length - grace);
    const rows = [];
    let balance = principal;
    for (const [index, dueDate] of dates.entries()) {
        if (balance <= 0)
            break;
        const isLast = index === dates.length - 1;
        const interestBase = (isFlat ? principal : balance);
        const interest = accruedInterest(interestBase, annualInterestRate, frequency, accrualPeriods);
        let principalPart;
        if (isLast) {
            principalPart = balance;
        }
        else if (fixedParts) {
            principalPart = fixedParts[index] ?? 0;
        }
        else if (index < grace) {
            principalPart = 0;
        }
        else {
            // En préstamos pequeños, largos y caros el redondeo puede dejar un
            // capital negativo; se recorta a cero y la última cuota absorbe el saldo.
            principalPart = Math.max(0, level - interest);
        }
        principalPart = Math.min(principalPart, balance);
        const tax = applyRate(interest, taxRate);
        rows.push({
            periodNumber: firstPeriodNumber + index,
            dueDate,
            openingBalance: balance,
            principal: principalPart,
            interest,
            tax,
            total: (principalPart + interest + tax),
            closingBalance: (balance - principalPart),
        });
        balance -= principalPart;
    }
    return rows;
}
/**
 * Vencimientos de todos los períodos del plazo (también los que no cobran
 * en un pago único): la grilla sobre la que se calcula el costo efectivo.
 */
export function loanDueDates(terms, calendar) {
    return buildDueDates({
        firstDueDate: terms.firstPaymentDate,
        frequency: terms.frequency,
        periods: terms.termPeriods,
        shiftToBusinessDay: terms.shiftToBusinessDay,
        ...(calendar ? { calendar } : {}),
    });
}
/** Plan completo a partir de las condiciones del préstamo y el calendario laboral de la agencia. */
export function buildSchedule(terms, calendar) {
    return amortizeTerms(terms, loanDueDates(terms, calendar));
}
/** Plan de las condiciones del préstamo sobre una grilla de vencimientos ya calculada. */
export function amortizeTerms(terms, dueDates) {
    return amortize({
        principal: terms.principal,
        annualInterestRate: terms.annualInterestRate,
        interestMethod: terms.interestMethod,
        repaymentMethod: terms.repaymentMethod,
        principalPlan: terms.principalPlan ?? null,
        frequency: terms.frequency,
        dueDates,
        principalGracePeriods: terms.principalGracePeriods,
        taxRate: terms.taxRate,
    });
}
//# sourceMappingURL=schedule.js.map